import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { HardDrive, Pencil, X, Check } from "lucide-react";

// Edit-icon trigger + modal to expand/shrink a cloud's S3 storage quota.
// Mirrors the backend guard (shrink only down to current usage + 1 GB buffer);
// the server enforces it authoritatively and applies the Garage quota.
export const StorageQuotaEditor = ({ cloudId, currentGb, usedGb, onSaved }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                data-testid="storage-edit-open"
                onClick={() => setOpen(true)}
                className="text-zinc-500 hover:text-cm-brand transition-colors"
                title="Adjust storage quota (expand/shrink)"
                aria-label="Adjust storage"
            >
                <Pencil size={12} />
            </button>
            {open && (
                <StorageQuotaModal
                    cloudId={cloudId}
                    currentGb={currentGb}
                    usedGb={usedGb}
                    onClose={() => setOpen(false)}
                    onSaved={onSaved}
                />
            )}
        </>
    );
};

function StorageQuotaModal({ cloudId, currentGb, usedGb, onClose, onSaved }) {
    const [val, setVal] = useState(currentGb || 50);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const used = Number(usedGb || 0);
    const minAllowed = Math.ceil(used + 1); // usage + 1 GB buffer
    const n = Number(val);
    const invalid = !Number.isFinite(n) || n < 10 || n > 10000;
    const tooSmall = n < minAllowed;
    const shrinking = n < (currentGb || 0);

    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    const save = async () => {
        setErr(null);
        setBusy(true);
        try {
            await api.put(`/api/admin/clouds/${cloudId}/storage`, { storage_gb: n });
            onSaved && onSaved();
            onClose();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="storage-modal-overlay"
        >
            <div
                className="bg-zinc-950 border border-zinc-700 max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="storage-editor"
            >
                <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <HardDrive size={16} className="text-cm-brand" />
                        <h2 className="cm-h3">Adjust storage quota</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="storage-edit-cancel"
                        className="text-zinc-400 hover:text-zinc-200"
                        aria-label="close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-sm">
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                        <span>Current quota</span>
                        <span className="font-mono text-zinc-200">{currentGb} GB</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-zinc-400">
                        <span>Current usage</span>
                        <span className="font-mono text-zinc-200">~{used.toFixed(2)} GB</span>
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-wide text-zinc-500">
                            New quota
                        </label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                type="number"
                                data-testid="storage-edit-input"
                                value={val}
                                min={10}
                                max={10000}
                                onChange={(e) => setVal(e.target.value)}
                                className="cm-input w-32 text-sm font-mono"
                            />
                            <span className="text-xs text-zinc-500">GB</span>
                        </div>
                    </div>

                    <div className="text-xs text-zinc-500 leading-relaxed bg-zinc-900 border border-zinc-800 px-3 py-2">
                        Shrinking is only possible down to the usage + 1 GB buffer —
                        minimum currently <span className="font-mono text-zinc-300">{minAllowed} GB</span>.
                        {tooSmall && (
                            <span className="text-amber-300 block mt-1" data-testid="storage-edit-warn">
                                ⚠ {n} GB is too small (min. {minAllowed} GB).
                            </span>
                        )}
                        {invalid && !tooSmall && (
                            <span className="text-amber-300 block mt-1">⚠ Allowed: 10–10000 GB.</span>
                        )}
                        {shrinking && !tooSmall && !invalid && (
                            <span className="text-zinc-400 block mt-1">
                                Note: shrinking sets a hard quota on the S3 bucket.
                            </span>
                        )}
                    </div>

                    {err && (
                        <div
                            className="bg-[#3a1010] border border-[#7a2a2a] p-3 text-xs text-[#ffaaaa] font-mono"
                            data-testid="storage-edit-err"
                        >
                            {err}
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                        <button
                            type="button"
                            data-testid="storage-edit-save"
                            disabled={busy || invalid || tooSmall}
                            onClick={save}
                            className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Check size={13} /> {busy ? "Saving…" : "Save"}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={busy}
                            className="cm-btn-ghost text-xs px-3 py-2 disabled:opacity-40"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
